/**
 * DataAgent Settings Store
 *
 * @package DataAgent
 * @since 1.1.0
 */

import { defineStore } from 'pinia';

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    // Provider
    providers: [],
    selectedProvider: 'openai',

    // Active tab in settings
    activeSettingsTab: 'api',

    // Save settings loading state
    saveSettingsLoading: false,

    // OpenAI settings
    openai: {
      apiKey: '',
      model: 'gpt-3.5-turbo',
      models: [],
      modelsLoading: false,
    },

    // Ollama settings
    ollama: {
      endpoint: 'http://localhost:11434',
      apiKey: '',
      model: 'llama2',
      models: [],
      modelsLoading: false,
    },

    // OpenRouter settings
    openrouter: {
      apiKey: '',
      model: 'deepseek/deepseek-chat-v3.1:free',
      models: [],
      modelsLoading: false,
    },

    // Notices
    notices: [],

    // Integrations
    integrations: {},
    activeIntegrations: {},
  }),

  actions: {
    /**
     * Set the active settings tab
     *
     * @param {string} tab Tab name
     */
    setActiveSettingsTab(tab) {
      this.activeSettingsTab = tab;
    },

    /**
     * Set the selected provider
     *
     * @param {string} provider Provider name
     */
    setProvider(provider) {
      this.selectedProvider = provider;
    },

    /**
     * Set OpenAI API key
     *
     * @param {string} apiKey API key
     */
    setOpenAIApiKey(apiKey) {
      this.openai.apiKey = apiKey;
    },

    /**
     * Set OpenAI model
     *
     * @param {string} model Model ID
     */
    setOpenAIModel(model) {
      this.openai.model = model;
    },

    /**
     * Set OpenAI models
     *
     * @param {Array} models Array of model objects
     */
    setOpenAIModels(models) {
      this.openai.models = models;
    },

    /**
     * Set OpenAI models loading state
     *
     * @param {boolean} loading Loading state
     */
    setOpenAIModelsLoading(loading) {
      this.openai.modelsLoading = loading;
    },

    /**
     * Set Ollama endpoint
     *
     * @param {string} endpoint Endpoint URL
     */
    setOllamaEndpoint(endpoint) {
      this.ollama.endpoint = endpoint;
    },

    /**
     * Set Ollama API key
     *
     * @param {string} apiKey API key
     */
    setOllamaApiKey(apiKey) {
      this.ollama.apiKey = apiKey;
    },

    /**
     * Set Ollama model
     *
     * @param {string} model Model ID
     */
    setOllamaModel(model) {
      this.ollama.model = model;
    },

    /**
     * Set Ollama models
     *
     * @param {Array} models Array of model objects
     */
    setOllamaModels(models) {
      this.ollama.models = models;
    },

    /**
     * Set Ollama models loading state
     *
     * @param {boolean} loading Loading state
     */
    setOllamaModelsLoading(loading) {
      this.ollama.modelsLoading = loading;
    },

    /**
     * Set OpenRouter API key
     *
     * @param {string} apiKey API key
     */
    setOpenRouterApiKey(apiKey) {
      this.openrouter.apiKey = apiKey;
    },

    /**
     * Set OpenRouter model
     *
     * @param {string} model Model ID
     */
    setOpenRouterModel(model) {
      this.openrouter.model = model;
    },

    /**
     * Set OpenRouter models
     *
     * @param {Array} models Array of model objects
     */
    setOpenRouterModels(models) {
      this.openrouter.models = models;
    },

    /**
     * Set OpenRouter models loading state
     *
     * @param {boolean} loading Loading state
     */
    setOpenRouterModelsLoading(loading) {
      this.openrouter.modelsLoading = loading;
    },

    /**
     * Add a notice to the settings interface
     *
     * @param {string} message The message to display
     * @param {string} type    The type of message (success, error, warning, info)
     */
    addNotice(message, type = 'info') {
      const notice = { message, type, id: Date.now() };
      this.notices.push(notice);

      // Remove the notice after 3 seconds
      setTimeout(() => {
        this.removeNotice(notice.id);
      }, 3000);
    },

    /**
     * Remove a notice from the settings interface
     *
     * @param {number} id ID of the notice to remove
     */
    removeNotice(id) {
      const index = this.notices.findIndex((notice) => notice.id === id);
      if (index !== -1) {
        this.notices.splice(index, 1);
      }
    },

    /**
     * Save settings to the server
     *
     * @returns {Promise} Promise that resolves when settings are saved
     */
    saveSettings() {
      this.saveSettingsLoading = true;

      return new Promise((resolve, reject) => {
        const settings = {
          api_provider: this.selectedProvider,

          openai_api_key: this.openai.apiKey,
          openai_model: this.openai.model,

          ollama_endpoint: this.ollama.endpoint,
          ollama_api_key: this.ollama.apiKey,
          ollama_model: this.ollama.model,

          openrouter_api_key: this.openrouter.apiKey,
          openrouter_model: this.openrouter.model,
        };

        // Save settings via AJAX
        jQuery.ajax({
          url: ajaxurl,
          type: 'POST',
          data: {
            action: 'dataagent_save_settings',
            settings: settings,
            nonce: DATAGENT_PLUGIN_SETTINGS.nonce,
          },
          success: (response) => {
            this.saveSettingsLoading = false;
            if (response.success) {
              this.addNotice('Settings saved successfully!', 'success');
              resolve(response);
            } else {
              this.addNotice('Failed to save settings: ' + response.message, 'error');
              reject(response);
            }
          },
          error: (xhr, status, error) => {
            this.saveSettingsLoading = false;
            this.addNotice('Failed to save settings. Please try again.', 'error');
            reject(error);
          },
        });
      });
    },

    /**
     * Fetch OpenAI models from the server
     */
    fetchOpenAIModels(force = true) {
      if (this.openai.apiKey.length < 20) return;

      this.openai.modelsLoading = true;
      if (force) {
        this.openai.model = '';
      }
      this.openai.models = [];

      // AJAX request to fetch models
      jQuery.ajax({
        url: ajaxurl,
        type: 'POST',
        data: {
          action: 'dataagent_fetch_openai_models',
          api_key: this.openai.apiKey,
          nonce: window.DATAGENT_PLUGIN_SETTINGS.nonce,
        },
        success: (response) => {
          this.openai.modelsLoading = false;

          if (response.success && response.data) {
            // Convert response data to array of objects
            this.openai.models = Object.entries(response.data).map(([id, text]) => ({
              id,
              text,
            }));
            if (force) {
              this.openai.model = this.openai.models[0].id;
            }
          } else {
            // Default model if error
            this.openai.models = [{ id: 'gpt-3.5-turbo', text: 'GPT-3.5 Turbo (default)' }];
            this.addNotice('Failed to fetch OpenAI models. Using default model.', 'error');
          }
        },
        error: () => {
          this.openai.modelsLoading = false;
          this.openai.models = [{ id: 'gpt-3.5-turbo', text: 'GPT-3.5 Turbo (default)' }];
          this.addNotice('Failed to fetch OpenAI models. Using default model.', 'error');
        },
      });
    },

    /**
     * Fetch Ollama models from the server
     */
    fetchOllamaModels(force = true) {
      if (this.ollama.endpoint.length < 10) return;

      this.ollama.modelsLoading = true;
      if (force) {
        this.ollama.model = '';
      }
      this.ollama.models = [];

      // AJAX request to fetch models
      jQuery.ajax({
        url: ajaxurl,
        type: 'POST',
        data: {
          action: 'dataagent_fetch_ollama_models',
          endpoint: this.ollama.endpoint,
          api_key: this.ollama.apiKey,
          nonce: window.DATAGENT_PLUGIN_SETTINGS.nonce,
        },
        success: (response) => {
          this.ollama.modelsLoading = false;

          if (response.success && response.data) {
            // Convert response data to array of objects
            this.ollama.models = Object.entries(response.data).map(([id, text]) => ({
              id,
              text,
            }));
            if (force) {
              this.ollama.model = this.ollama.models[0].id;
            }
          } else {
            // Default model if error
            this.ollama.models = [{ id: 'llama2', text: 'Llama 2 (default)' }];
            this.addNotice('Failed to fetch Ollama models. Using default model.', 'error');
          }
        },
        error: () => {
          this.ollama.modelsLoading = false;
          this.ollama.models = [{ id: 'llama2', text: 'Llama 2 (default)' }];
          this.addNotice('Failed to fetch Ollama models. Using default model.', 'error');
        },
      });
    },

    /**
     * Fetch OpenRouter models from the server
     */
    fetchOpenRouterModels(force = true) {
      if (this.openrouter.apiKey.length < 20) return;

      this.openrouter.modelsLoading = true;
      if (force) {
        this.openrouter.model = '';
      }
      this.openrouter.models = [];

      // AJAX request to fetch models
      jQuery.ajax({
        url: ajaxurl,
        type: 'POST',
        data: {
          action: 'dataagent_fetch_openrouter_models',
          api_key: this.openrouter.apiKey,
          nonce: window.DATAGENT_PLUGIN_SETTINGS.nonce,
        },
        success: (response) => {
          this.openrouter.modelsLoading = false;

          if (response.success && response.data) {
            // Convert response data to array of objects
            this.openrouter.models = Object.entries(response.data).map(([id, text]) => ({
              id,
              text,
            }));
            if (force) {
              this.openrouter.model = this.openrouter.models[0].id;
            }
          } else {
            // Default model if error
            this.openrouter.models = [
              {
                id: 'deepseek/deepseek-chat-v3.1:free',
                text: 'Deepseek Chat v3.1 (free)',
              },
            ];
            this.addNotice('Failed to fetch OpenRouter models. Using default model.', 'error');
          }
        },
        error: () => {
          this.openrouter.modelsLoading = false;
          this.openrouter.models = [
            {
              id: 'deepseek/deepseek-chat-v3.1:free',
              text: 'Deepseek Chat v3.1 (free)',
            },
          ];
          this.addNotice('Failed to fetch OpenRouter models. Using default model.', 'error');
        },
      });
    },

    /**
     * Load settings from the server
     *
     * @returns {Object} Settings object
     */
    loadSettings() {
      // Load settings from WordPress global variable
      if (typeof window.DATAGENT_PLUGIN_SETTINGS !== 'undefined') {
        // Load settings
        if (window.DATAGENT_PLUGIN_SETTINGS.settings) {
          const settings = window.DATAGENT_PLUGIN_SETTINGS.settings;

          // Set provider
          if (settings.providers) {
            this.providers = settings.providers;
          }

          if (settings.provider) {
            this.selectedProvider = settings.provider;
          }

          // Set OpenAI settings
          if (settings.openai) {
            this.openai.apiKey = settings.openai.api_key || '';
            this.openai.model = settings.openai.model || '';
            if (this.openai.apiKey) {
              this.fetchOpenAIModels(false);
            }
          }

          // Set Ollama settings
          if (settings.ollama) {
            this.ollama.endpoint = settings.ollama.endpoint || '';
            this.ollama.apiKey = settings.ollama.api_key || '';
            this.ollama.model = settings.ollama.model || '';
            if (this.ollama.endpoint) {
              this.fetchOllamaModels(false);
            }
          }

          // Set OpenRouter settings
          if (settings.openrouter) {
            this.openrouter.apiKey = settings.openrouter.api_key || '';
            this.openrouter.model = settings.openrouter.model || '';
            if (this.openrouter.apiKey) {
              this.fetchOpenRouterModels(false);
            }
          }
        }

        // Load integrations
        if (window.DATAGENT_PLUGIN_SETTINGS.integrations) {
          this.integrations = window.DATAGENT_PLUGIN_SETTINGS.integrations;
        }

        // Load active integrations
        if (window.DATAGENT_PLUGIN_SETTINGS.active_integrations) {
          this.activeIntegrations = window.DATAGENT_PLUGIN_SETTINGS.active_integrations;
        }

        return window.DATAGENT_PLUGIN_SETTINGS.settings || null;
      }

      return null;
    },
  },
});
